import { useApiUrl, useCustomMutation } from "@refinedev/core";
import { SmlJsonQuery, SmlJsonQueryOrder, SmlJsonQueryFilter } from "@src/types/intelligence-search";
import React from "react";
import { useSmartCustomMutation } from "../use-smart-custom-mutation";

export const convertFilterToPayload = (
    filters: SmlJsonQueryFilter[],
    orders: SmlJsonQueryOrder[],
    limit?: number,
): SmlJsonQuery => {
    const extractFilter = filters.reduce(
        (acc, currItem) => {
            if (!currItem.meta) return acc;
            if (!currItem.operator) {
                if (currItem.isCustomMetric) {
                    acc.select?.push({
                        alias: currItem.meta.name,
                        expr: currItem.meta.id,
                    });
                } else {
                    acc.select?.push(currItem.name);
                }
            } else {
                const valuesFilter = Array.isArray(currItem.values)
                    ? currItem.values
                    : currItem.values !== null && currItem.values !== undefined
                    ? [currItem.values]
                    : [];
                acc.filters?.push({
                    name: currItem.meta.name,
                    operator: currItem.operator,
                    values: valuesFilter,
                });
            }
            return acc;
        },
        {
            select: [],
            filters: [],
        } as Omit<SmlJsonQuery, "orders" | "limit">,
    );

    return {
        select: extractFilter.select,
        filters: extractFilter.filters,
        orders,
        limit,
    };
};

export const useFetchNextOption = ({
    productCode,
    startDate,
    endDate,
}: {
    productCode?: string;
    startDate?: string;
    endDate?: string;
}) => {
    const apiUrl = useApiUrl("semantic");
    const { debouncedMutateAsync, isLoading: isLoadingAnalyze } = useSmartCustomMutation();
    const fetchAnalyze = React.useCallback(
        async (payload: SmlJsonQuery) => {
            const result = await debouncedMutateAsync(
                {
                    method: "post",
                    url: apiUrl + "/analyze-potential-assets",
                    values: payload,
                    successNotification: false,
                    errorNotification: false,
                    config: {
                        headers: {
                            "product-code": productCode,
                            "start-date": startDate,
                            "end-date": endDate,
                        },
                    },
                },
                300,
            );
            return {
                meta: result?.meta ?? [],
                unlikely_meta: result?.unlikely_meta ?? [],
                selected_assets: result?.selected_assets ?? [],
            };
        },
        [debouncedMutateAsync, apiUrl, productCode, startDate, endDate],
    );

    const fetchDryrun = React.useCallback(
        async (payload: SmlJsonQuery) => {
            const result = await debouncedMutateAsync(
                {
                    method: "post",
                    url: apiUrl + "/dry-run",
                    values: payload,
                    successNotification: false,
                    errorNotification: false,
                    config: {
                        headers: {
                            "product-code": productCode,
                            "start-date": startDate,
                            "end-date": endDate,
                        },
                    },
                },
                300,
            );
            return result?.meta ?? [];
        },
        [debouncedMutateAsync, apiUrl, productCode, startDate, endDate],
    );
    return { fetchAnalyze, isLoadingAnalyze, fetchDryrun };
};
